// IM-JRN-012..014 (IM-UX-147..149) — "Base financeira" dentro do objetivo Planejar: grade anual, grade mensal, ofertas e
// planilha (baixar modelo → preencher → enviar → validar → conferir prévia → importar). Tudo grava na MESMA base, pelas
// mesmas regras; nada aqui é contabilidade.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DATASET_LABEL, fmtBRL, objectiveOf, PLANNING_TEMPLATE_VERSION, type PlanningDataset, type PlanningPreviewDto } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { AsyncView } from '../components/States';
import { TabsNav } from '../components/TabsNav';
import { useToast } from '../components/Toast';
import { fileToBase64, planningApi } from '../data/planning';
import { useResource } from '../hooks/useResource';
import { ObjectiveHead } from './ObjectiveWorkspace';
import { PlanGrid, type GridMode } from './PlanGrid';

const TABS = [['anual', 'Grade anual'], ['mensal', 'Grade mensal'], ['ofertas', 'Ofertas'], ['planilha', 'Planilha (XLSX ou CSV)']] as const;
type Tab = (typeof TABS)[number][0];

export function PlanData() {
  const def = objectiveOf('planejar')!;
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find(([key]) => key === params.get('aba'))?.[0] ?? 'anual') as Tab;
  const { state, reload } = useResource((s) => Promise.all([planningApi.financials(s), planningApi.plans(s)]).then(([f, p]) => ({ ...f, suggestedYear: p.suggestedYear })), []);
  const go = (key: Tab) => { const p = new URLSearchParams(params); p.set('aba', key); setParams(p, { replace: true }); };
  return (
    <section aria-labelledby="page-title">
      <ObjectiveHead def={def} journey={null} hideAnalyses>
        <Link className="secondary btn-link" to="/objetivos/planejar">Voltar ao plano</Link>
      </ObjectiveHead>
      <div className="xp-shell">
        <div className="xp-overview"><div><b>Base financeira</b><span>Grandes números por ano ou por mês. Comece só com os totais anuais; detalhe por mês quando tiver. Obrigatórios: faturamento e despesas.</span></div></div>
        <TabsNav label="Como informar os dados" active={tab}>
          {TABS.map(([key, label]) => <a key={key} href={`?aba=${key}`} className={tab === key ? 'active' : ''} aria-current={tab === key ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(key); }}>{label}</a>)}
        </TabsNav>
        <AsyncView state={state} onRetry={reload} empty={null}>
          {(d) => tab === 'planilha' ? <ImportPanel year={d.suggestedYear} onDone={reload} /> : <GridPanel mode={tab === 'anual' ? 'ANUAL' : tab === 'mensal' ? 'MENSAL' : 'OFERTAS'} d={d} onSaved={reload} />}
        </AsyncView>
        <details className="xp-why pl-help">
          <summary>O que é cada número?</summary>
          <dl className="pl-dl">
            <div><dt>Faturamento</dt><dd>Tudo o que a empresa faturou no período, já sem devoluções.</dd></div>
            <div><dt>Despesas</dt><dd>Tudo o que a empresa gastou para operar: custos, pessoal, comercial e outras. As colunas opcionais detalham partes desse total.</dd></div>
            <div><dt>Custos diretos</dt><dd>A parte das despesas ligada diretamente ao que foi vendido.</dd></div>
            <div><dt>Realizado</dt><dd>O que de fato aconteceu num mês já encerrado. É comparado com a meta no acompanhamento do plano.</dd></div>
            <div><dt>Planejado</dt><dd>A meta do plano aprovado para o mês ou para o ano.</dd></div>
          </dl>
        </details>
      </div>
    </section>
  );
}

function GridPanel({ mode, d, onSaved }: { mode: GridMode; d: Awaited<ReturnType<typeof planningApi.financials>> & { suggestedYear: number }; onSaved: () => void }) {
  const years = [...new Set([d.suggestedYear - 1, d.suggestedYear - 2, d.suggestedYear - 3, ...d.periods.map((p) => p.year)])].filter((y) => y < d.suggestedYear).sort((a, b) => b - a);
  const [year, setYear] = useState(years[0]!);
  return (
    <article className="xp-panel">
      <div className="pl-panel-head">
        <div>
          <span className="xp-kicker">{mode === 'ANUAL' ? 'Visão anual' : mode === 'MENSAL' ? 'Visão mensal' : 'Produtos e ofertas'}</span>
          <h2>{mode === 'ANUAL' ? 'Um total por ano' : mode === 'MENSAL' ? `Mês a mês em ${year}` : `Ofertas em ${year}`}</h2>
          <p className="cell-sub">{mode === 'ANUAL' ? 'O jeito mais rápido de começar.' : mode === 'MENSAL' ? 'Com os 12 meses, o InteliMove passa a usar a sazonalidade e o total do ano vem da soma dos meses.' : 'Use as ofertas já cadastradas. Tudo opcional: preencha o que souber.'}</p>
        </div>
        {mode !== 'ANUAL' && (
          <label className="pl-year-pick">Ano <select value={year} onChange={(e) => setYear(Number(e.target.value))}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        )}
      </div>
      {mode === 'OFERTAS' && !d.offerings.length
        ? <p className="xp-quiet">Nenhuma oferta confirmada ainda. <Link to="/configuracoes/contexto/ofertas">Cadastre as ofertas</Link> no contexto da empresa.</p>
        : <PlanGrid mode={mode} year={year} periods={d.periods} offerings={d.offerings} metrics={d.offeringMetrics} onSaved={onSaved} />}
    </article>
  );
}

const KIND_LABEL: Record<string, string> = { NOVO: 'Novo', ATUALIZA: 'Atualiza', IGUAL: 'Sem mudança', MANTIDO: 'Mantido' };
function ImportPanel({ year, onDone }: { year: number; onDone: () => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [csvDataset, setCsvDataset] = useState<PlanningDataset>('FINANCEIRO');
  const [preview, setPreview] = useState<PlanningPreviewDto | null>(null);
  const [strategy, setStrategy] = useState<'COMPLEMENTAR' | 'ATUALIZAR' | ''>('');
  const [busy, setBusy] = useState(false);
  const imports = useResource((s) => planningApi.imports(s), []);
  const isCsv = !!file && /\.csv$|\.txt$/i.test(file.name);
  const send = async (apply: boolean) => {
    if (!file) return;
    setBusy(true);
    try {
      const contentBase64 = await fileToBase64(file);
      const r = await planningApi.importFile({ fileName: file.name, contentBase64, csvDataset: isCsv ? csvDataset : undefined, strategy: apply ? (strategy || undefined) : undefined, apply });
      if (apply) {
        toast(r.alreadyImported ? 'Este arquivo já tinha sido importado com essa escolha. Nada foi alterado.' : `Importado: ${r.inserts} novos, ${r.updates} atualizados, ${r.unchanged} sem mudança, ${r.skipped} mantidos.`);
        setPreview(null); setFile(null); setStrategy(''); imports.reload(); onDone();
      } else setPreview(r);
    } catch (e) {
      const det = e instanceof ApiError ? (e.details as PlanningPreviewDto | undefined) : undefined;
      if (det?.issues) setPreview(det);
      toast(e instanceof ApiError ? e.message : 'Não foi possível ler o arquivo.', 'error');
    } finally { setBusy(false); }
  };
  return (
    <div className="xp-two">
      <article className="xp-panel">
        <span className="xp-kicker">Planilha</span>
        <h2>Baixe o modelo, preencha e envie</h2>
        <ol className="pl-steps" aria-label="Etapas da importação"><li>Baixar modelo</li><li>Preencher</li><li>Enviar</li><li>Validar</li><li>Conferir prévia</li><li>Importar</li></ol>
        <div className="xp-cta-row">
          <a className="secondary btn-link" href={planningApi.templateHref(year)} download>Baixar a planilha-modelo (versão {PLANNING_TEMPLATE_VERSION})</a>
        </div>
        <div className="pl-upload">
          <label className="field">Arquivo preenchido (.xlsx ou .csv, até 2 MB)
            <input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); }} />
          </label>
          {isCsv && (
            <label className="field">O que o CSV contém?
              <select value={csvDataset} onChange={(e) => setCsvDataset(e.target.value as PlanningDataset)}>{(['FINANCEIRO', 'OFERTAS', 'REALIZADO'] as const).map((k) => <option key={k} value={k}>{DATASET_LABEL[k]}</option>)}</select>
            </label>
          )}
          <button type="button" className="xp-primary petrol" disabled={!file || busy} onClick={() => send(false)}>{busy && !preview ? 'Validando…' : 'Validar o arquivo'}</button>
          <p className="cell-sub">Validar não grava nada. Macros e fórmulas externas não são executadas; o arquivo não fica guardado.</p>
        </div>
        {preview && <PreviewView p={preview} strategy={strategy} setStrategy={setStrategy} busy={busy} onImport={() => send(true)} onCancel={() => { setPreview(null); setStrategy(''); }} />}
      </article>
      <aside className="xp-panel" aria-labelledby="pl-imp-h">
        <h3 id="pl-imp-h">Importações anteriores</h3>
        <AsyncView state={imports.state} onRetry={imports.reload} empty={null}>
          {(d) => d.items.length ? (
            <ul className="xp-list" role="list">{d.items.slice(0, 8).map((i) => <li key={i.id} className="xp-row"><div className="xp-row-main"><b>{i.fileName}</b><span>{i.format}{i.templateVersion ? ` · modelo ${i.templateVersion}` : ''} · {i.strategy === 'COMPLEMENTAR' ? 'complementar' : 'atualizar'} · {i.inserted} novos, {i.updated} atualizados</span><span>{i.by ?? 'Sistema'} · {new Date(i.at).toLocaleString('pt-BR')}</span></div></li>)}</ul>
          ) : <p className="xp-quiet">Nenhuma planilha importada ainda.</p>}
        </AsyncView>
      </aside>
    </div>
  );
}

function PreviewView({ p, strategy, setStrategy, busy, onImport, onCancel }: { p: PlanningPreviewDto; strategy: string; setStrategy: (s: 'COMPLEMENTAR' | 'ATUALIZAR') => void; busy: boolean; onImport: () => void; onCancel: () => void }) {
  const errors = p.issues.filter((i) => i.severity === 'ERRO');
  const warnings = p.issues.filter((i) => i.severity === 'AVISO');
  const existing = p.updates + p.unchanged + p.skipped;
  return (
    <section className="pl-preview" aria-labelledby="pl-prev-h">
      <h3 id="pl-prev-h">Prévia da importação</h3>
      <ul className="metric-row">
        <li><b>{p.format ?? '—'}</b><span>{p.templateVersion ? `Modelo ${p.templateVersion}` : 'Arquivo reconhecido'}</span></li>
        <li><b>{p.periods.length}</b><span>{p.periods.length === 1 ? 'período' : 'períodos'}{p.periods.length ? `: ${p.periods.slice(0, 4).join(', ')}${p.periods.length > 4 ? '…' : ''}` : ''}</span></li>
        <li><b>{fmtBRL(p.totals.revenue)}</b><span>Faturamento somado</span></li>
        <li><b>{p.inserts}</b><span>{p.inserts === 1 ? 'linha nova' : 'linhas novas'} · {existing} já {existing === 1 ? 'existe' : 'existem'}</span></li>
      </ul>
      {p.alreadyImported && <p className="xp-block" role="note">Este mesmo arquivo já foi importado. Importar de novo com a mesma escolha não altera nada.</p>}
      {errors.length > 0 && (
        <div role="alert">
          <p className="pl-bad"><b>{errors.length} {errors.length === 1 ? 'erro impede' : 'erros impedem'} a importação.</b> Corrija na planilha e envie de novo — nada foi gravado.</p>
          <IssueTable items={errors} />
        </div>
      )}
      {warnings.length > 0 && <details className="xp-why" open={errors.length === 0}><summary>{warnings.length} {warnings.length === 1 ? 'aviso' : 'avisos'}</summary><IssueTable items={warnings} /></details>}
      {p.changes.length > 0 && (
        <details className="xp-why"><summary>O que vai mudar ({p.changes.length})</summary>
          <ul className="xp-steps-detail">{p.changes.slice(0, 60).map((c, i) => <li key={i}><span><b>{c.label}</b> <span className="cell-sub">{c.detail}</span></span><span className={`tag ${c.kind === 'NOVO' ? 'mint' : c.kind === 'ATUALIZA' ? 'amber' : 'gray'}`}>{KIND_LABEL[c.kind]}</span></li>)}</ul>
        </details>
      )}
      {errors.length === 0 && (
        <fieldset className="pl-strategy">
          <legend>Períodos que já existem na base ({existing})</legend>
          <label><input type="radio" name="pl-strategy" checked={strategy === 'COMPLEMENTAR'} onChange={() => setStrategy('COMPLEMENTAR')} /> <b>Complementar</b> — gravar só os períodos que faltam; os existentes ficam como estão.</label>
          <label><input type="radio" name="pl-strategy" checked={strategy === 'ATUALIZAR'} onChange={() => setStrategy('ATUALIZAR')} /> <b>Atualizar</b> — substituir os existentes pelos valores da planilha (o valor anterior fica no histórico).</label>
          <p className="cell-sub">Nada que não está na planilha é apagado.</p>
          <div className="pl-inline-actions">
            <button type="button" className="xp-primary petrol" disabled={!strategy || busy} onClick={onImport}>{busy ? 'Importando…' : 'Importar'}</button>
            <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>
          </div>
        </fieldset>
      )}
    </section>
  );
}

function IssueTable({ items }: { items: PlanningPreviewDto['issues'] }) {
  return (
    <div className="pl-scroll">
      <table className="pl-table">
        <thead><tr><th scope="col">Aba</th><th scope="col">Linha</th><th scope="col">Coluna</th><th scope="col">Valor</th><th scope="col">O que fazer</th></tr></thead>
        <tbody>{items.slice(0, 50).map((i, n) => <tr key={n}><td>{i.sheet ?? '—'}</td><td>{i.line ?? '—'}</td><td>{i.column ?? '—'}</td><td>{i.value ?? '—'}</td><td><b>{i.message}</b> {i.expected}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
